import { useState } from "react";
import { Reveal } from "./Reveal";
import { CheckCircle2, Loader2, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const describeYouOptions = [
  "I want to start my first trucking company",
  "I already own another business",
  "I currently work in trucking",
  "I currently own a trucking company",
  "I currently own one or more trucks",
  "I'm researching trucking as a business opportunity",
];

const ownTruckOptions = ["Yes", "No", "Currently shopping", "Not sure what I need yet"];

const authorityOptions = ["Yes", "No", "In progress", "I'm not sure what that means"];

const goalOptions = [
  "Start with one truck",
  "Build a small fleet",
  "Build a larger transportation company",
  "Have I Stay Trucking manage my existing operation",
  "I'm still exploring",
];

const beginOptions = ["As soon as possible", "Within 30 days", "1-3 months", "3-6 months", "I'm still researching"];

const capitalOptions = ["Yes", "I need to understand the costs first", "I may need financing", "Not yet"];

const initialForm = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  city: "",
  state: "",
  describe_you: "",
  own_truck: "",
  authority: "",
  goal: "",
  begin_timeline: "",
  capital: "",
  about: "",
};

const inputCls =
  "w-full bg-[#0D0D0D] border border-neutral-700 px-4 py-4 text-white placeholder-neutral-600 outline-none transition-colors duration-200 focus:border-orange-500";
const labelCls = "block mb-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-400";

const OptionGroup = ({ options, value, onSelect, testIdPrefix }) => (
  <div className="grid grid-cols-2 gap-3">
    {options.map((o) => (
      <button
        key={o}
        type="button"
        data-testid={`${testIdPrefix}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        onClick={() => onSelect(o)}
        className={`border px-4 py-3 text-sm font-semibold uppercase tracking-wide transition-colors duration-200 ${
          value === o
            ? "border-orange-500 bg-orange-500/10 text-orange-500"
            : "border-neutral-700 text-neutral-400 hover:border-neutral-500"
        }`}
      >
        {o}
      </button>
    ))}
  </div>
);

export const LeadForm = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const required = ["describe_you", "own_truck", "authority", "goal", "begin_timeline", "capital", "about"];
    if (required.some((k) => !form[k])) {
      setError("Please answer every question before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail = data.detail;
        throw new Error(
          typeof detail === "string"
            ? detail
            : "Something went wrong. Please check your information and try again."
        );
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="get-started" data-testid="lead-form-section" className="bg-[#0A0A0A] py-24 md:py-32 border-y border-neutral-800">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid md:grid-cols-5 gap-12 md:gap-16">
          <Reveal className="md:col-span-2">
            <p className="font-display text-sm font-semibold uppercase tracking-[0.3em] text-orange-500 mb-6" data-testid="form-eyebrow">
              Qualify Now
            </p>
            <h2
              className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight leading-[1.05] text-white"
              data-testid="form-headline"
            >
              Let's Talk About The Company You Want To Build.
            </h2>
            <p className="mt-8 text-base md:text-lg text-neutral-300" data-testid="form-subheadline">
              Answer a few questions so our team can understand your goals and determine whether I
              Stay Trucking may be a fit.
            </p>
            <ul className="mt-10 space-y-4 text-sm md:text-base text-neutral-400">
              <li className="flex items-center gap-3">
                <span className="h-px w-8 bg-orange-500" /> A confidential discovery call
              </li>
              <li className="flex items-center gap-3">
                <span className="h-px w-8 bg-orange-500" /> A real conversation about your goals
              </li>
              <li className="flex items-center gap-3">
                <span className="h-px w-8 bg-orange-500" /> Details and numbers presented privately
              </li>
            </ul>
          </Reveal>

          <Reveal delay={0.15} className="md:col-span-3">
            {submitted ? (
              <div
                data-testid="form-success-message"
                className="h-full border-2 border-orange-500 bg-[#121212] p-10 md:p-14 flex flex-col items-start justify-center"
              >
                <CheckCircle2 size={48} className="text-orange-500" />
                <h3 className="mt-8 font-display text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
                  Your Company Starts With A Conversation.
                </h3>
                <p className="mt-6 text-base md:text-lg text-neutral-300">
                  Thanks for reaching out to I Stay Trucking. Our team will review your information
                  and contact you to discuss your goals, your timeline and whether our operating
                  model makes sense for your situation.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="lead-form"
                className="border border-neutral-800 bg-[#121212] p-8 md:p-10"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="first_name" className={labelCls}>First Name</label>
                    <input id="first_name" data-testid="input-first-name" required className={inputCls} value={form.first_name} onChange={set("first_name")} placeholder="John" />
                  </div>
                  <div>
                    <label htmlFor="last_name" className={labelCls}>Last Name</label>
                    <input id="last_name" data-testid="input-last-name" required className={inputCls} value={form.last_name} onChange={set("last_name")} placeholder="Smith" />
                  </div>
                  <div>
                    <label htmlFor="phone" className={labelCls}>Phone</label>
                    <input id="phone" data-testid="input-phone" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" />
                  </div>
                  <div>
                    <label htmlFor="email" className={labelCls}>Email</label>
                    <input id="email" data-testid="input-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                  </div>
                  <div>
                    <label htmlFor="city" className={labelCls}>City</label>
                    <input id="city" data-testid="input-city" required className={inputCls} value={form.city} onChange={set("city")} placeholder="Dallas" />
                  </div>
                  <div>
                    <label htmlFor="state" className={labelCls}>State</label>
                    <input id="state" data-testid="input-state" required className={inputCls} value={form.state} onChange={set("state")} placeholder="TX" />
                  </div>
                </div>

                <div className="mt-8">
                  <label htmlFor="describe_you" className={labelCls}>What best describes you?</label>
                  <select
                    id="describe_you"
                    data-testid="select-describe-you"
                    required
                    className={`${inputCls} appearance-none ${form.describe_you ? "text-white" : "text-neutral-600"}`}
                    value={form.describe_you}
                    onChange={set("describe_you")}
                  >
                    <option value="" disabled>Select one</option>
                    {describeYouOptions.map((s) => (
                      <option key={s} value={s} className="text-white">{s}</option>
                    ))}
                  </select>
                </div>

                <div className="mt-8">
                  <span className={labelCls}>Do you currently own a truck?</span>
                  <OptionGroup options={ownTruckOptions} value={form.own_truck} onSelect={(v) => setForm({ ...form, own_truck: v })} testIdPrefix="own-truck" />
                </div>

                <div className="mt-8">
                  <span className={labelCls}>Do you currently have trucking authority?</span>
                  <OptionGroup options={authorityOptions} value={form.authority} onSelect={(v) => setForm({ ...form, authority: v })} testIdPrefix="authority" />
                </div>

                <div className="mt-8">
                  <label htmlFor="goal" className={labelCls}>What is your goal?</label>
                  <select
                    id="goal"
                    data-testid="select-goal"
                    required
                    className={`${inputCls} appearance-none ${form.goal ? "text-white" : "text-neutral-600"}`}
                    value={form.goal}
                    onChange={set("goal")}
                  >
                    <option value="" disabled>Select your goal</option>
                    {goalOptions.map((s) => (
                      <option key={s} value={s} className="text-white">{s}</option>
                    ))}
                  </select>
                </div>

                <div className="mt-8">
                  <span className={labelCls}>When would you like to begin?</span>
                  <OptionGroup options={beginOptions} value={form.begin_timeline} onSelect={(v) => setForm({ ...form, begin_timeline: v })} testIdPrefix="begin" />
                </div>

                <div className="mt-8">
                  <label htmlFor="capital" className={labelCls}>
                    Are you prepared to invest capital into starting and operating your trucking company?
                  </label>
                  <select
                    id="capital"
                    data-testid="select-capital"
                    required
                    className={`${inputCls} appearance-none ${form.capital ? "text-white" : "text-neutral-600"}`}
                    value={form.capital}
                    onChange={set("capital")}
                  >
                    <option value="" disabled>Select one</option>
                    {capitalOptions.map((s) => (
                      <option key={s} value={s} className="text-white">{s}</option>
                    ))}
                  </select>
                </div>

                <div className="mt-8">
                  <label htmlFor="about" className={labelCls}>Tell us about what you're trying to build.</label>
                  <textarea
                    id="about"
                    data-testid="input-about"
                    required
                    rows={4}
                    className={`${inputCls} resize-none`}
                    value={form.about}
                    onChange={set("about")}
                    placeholder="Your goals, timeline, and where you are in the process..."
                  />
                </div>

                {error && (
                  <p data-testid="form-error-message" className="mt-6 border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="lead-form-submit-button"
                  disabled={submitting}
                  className="mt-10 flex w-full items-center justify-center gap-2 bg-orange-500 px-8 py-5 font-display text-lg md:text-xl font-bold uppercase tracking-wider text-white transition-all duration-200 hover:bg-orange-600 disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={22} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      See If I Qualify
                      <ChevronRight size={22} />
                    </>
                  )}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
